import {
    Component,
    Input,
    Output,
    EventEmitter,
    forwardRef,
    ElementRef,
    ViewEncapsulation,
    HostListener
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CalendarModule } from '@progress/kendo-angular-dateinputs';

@Component({
    selector: 'nova-kendo-multiple-date-picker',
    templateUrl: './kendo-multiple-date-picker.component.html',
    styleUrls: ['./kendo-multiple-date-picker.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => KendoMultipleDatePickerComponent),
            multi: true
        }
    ],
    standalone: true,
    imports: [CalendarModule, FormsModule]
})
export class KendoMultipleDatePickerComponent implements ControlValueAccessor {
    @Input() placeholderText = 'Select dates...';
    @Input() calendarType: 'infinite' | 'classic' = 'classic';
    @Input() disabledDates: Date[];
    @Input() selectionMode: 'single' | 'multiple' = 'multiple';
    @Input() minDate = new Date();
    @Input() maxDate: Date;
    @Input() isEnabled = true;
    @Input() selectedDates: Date[] = [];
    @Input() isError = false;

    @Output() selectedDatesChange = new EventEmitter<Date[]>();

    private _showCalendar = false;
    get showCalendar(): boolean {
        return this._showCalendar;
    }

    set showCalendar(value: boolean) {
        if (this._showCalendar && !value) {
            this.onTouchedCallback();
        }
        this._showCalendar = value;
    }

    // The internal data model
    innerValue: Date[] = [];

    constructor(private readonly _elRef: ElementRef) {}

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this._elRef.nativeElement.contains(event.target)) {
            this.showCalendar = false;
        }
    }

    handleClick(event: MouseEvent) {
        const clickedItem = (event.target as HTMLElement).closest("td[role='gridcell']");
        if (clickedItem) {
            this.selectedDates = this.innerValue;
            this.selectedDatesChange.emit(this.selectedDates);
            this.onChangeCallback(this.innerValue);
            event.stopPropagation();
            event.preventDefault();
        }
    }

    onDateClick(event: any): void {
        // Retrieve the closest parent grid cell of the clicked element
        const clickedItem = event.target.closest("td[role='gridcell']");

        // exit if clickedItem is null ot there is no title
        if (!clickedItem?.title) {
            return;
        }

        // Check if textContent is numeric not a month name
        const textContent = clickedItem.textContent.trim();
        if (!/^\d+$/.test(textContent)) {
            return;
        }

        // Check if textContent is year
        if (textContent.length === 4 || parseInt(textContent) > 31) {
            return;
        }

        // parse the title to a Date object
        const clickedDate = new Date(clickedItem.title);

        // Ensure clickedDate is a valid date.
        if (isNaN(clickedDate.getTime())) {
            return;
        }

        // Validate against the minimum date allowed
        if (this.isBeforeMinDate(clickedDate, this.minDate)) {
            return;
        }

        // Find the index of the clicked date in the inner value array
        const index = this.innerValue.findIndex(
            (date) =>
                date.getFullYear() === clickedDate.getFullYear() &&
                date.getMonth() === clickedDate.getMonth() &&
                date.getDate() === clickedDate.getDate()
        );

        if (index > -1) {
            // If the date is found, remove it from the array
            this.innerValue.splice(index, 1);
            clickedItem.classList.remove('k-selected');
        } else {
            // Otherwise, add the new date
            this.innerValue.push(clickedDate);
        }

        // Update selected dates to reflect the new state
        this.selectedDates = [...this.innerValue];
    }

    onInputBlur() {
        setTimeout(() => {
            this.showCalendar = false;
        }, 50);
    }

    formatDates(dates: Date[]): string {
        if (!dates || !Array.isArray(dates) || dates.length === 0) {
            return '';
        }

        //sort the dates array in ascending order
        const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());

        return sortedDates
            .map((date) => {
                const day = date.getDate();
                const month = date.getMonth() + 1;
                const year = date.getFullYear();

                // Ensure two digits for day and month
                const dayStr = day < 10 ? '0' + day : '' + day;
                const monthStr = month < 10 ? '0' + month : '' + month;

                return `${monthStr}/${dayStr}/${year}`;
            })
            .join(', ');
    }

    // Implement the ControlValueAccessor methods

    // Write a new value to the element
    writeValue(value: any): void {
        // do nothing
    }

    // Register a callback function that should be called when the control's value changes in the UI
    registerOnChange(fn: any): void {
        this.onChangeCallback = fn;
    }

    // Register a callback function that should be called when the control receives a blur event
    registerOnTouched(fn: any): void {
        this.onTouchedCallback = fn;
    }

    // This function is called by the forms API when the control status changes to or from "DISABLED"
    setDisabledState?(isDisabled: boolean): void {
        this.isEnabled = !isDisabled;
    }

    // Compares clicked-date and min-date based on their year, month and day parts
    // true if clickedDate is less than minDate otherwise, false
    isBeforeMinDate(clickedDate: Date, minDate: Date): boolean {
        const result =
            clickedDate.getFullYear() < minDate.getFullYear() ||
            (clickedDate.getFullYear() === minDate.getFullYear() && clickedDate.getMonth() < minDate.getMonth()) ||
            (clickedDate.getFullYear() === minDate.getFullYear() &&
                clickedDate.getMonth() === minDate.getMonth() &&
                clickedDate.getDate() < minDate.getDate());
        return result;
    }

    private onTouchedCallback: () => void = () => {};

    private onChangeCallback: (_: any) => void = () => {};
}
